import type { SidepanelTabPropsTypes, SidepanelTabsPropsTypes } from '@Shelly/shelly-design-system';
import { defaultStateType } from 'src/stores/device-runtime/states';
import { checkDeviceForceUpdate } from 'src/helpers/firmware_helpers';
import type { Device } from 'src/types/Device';

export const DEFAULT_TABS_CONFIG: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-sliders-simple',
		isActive: true,
	},
	{
		icon: 'fat fa-palette',
		isActive: false,
	},
	{
		icon: 'fat fa-circle-info',
		isActive: false,
	},
];

export const roomsTabsConfig: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-sliders-simple',
		isActive: true,
		disabled: false,
		id: 'main',
	},
	{
		icon: 'fat fa-microchip',
		isActive: false,
		disabled: false,
		id: 'devices',
	},
	{
		icon: 'fat fa-circle-info',
		isActive: false,
		disabled: false,
		id: 'info',
	},
];

export const scenesTabsConfig: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-sliders-simple',
		isActive: true,
		disabled: false,
		id: 'main',
	},
	{
		icon: 'fat fa-play',
		isActive: false,
		disabled: false,
		id: 'play',
	},
	{
		icon: 'fat fa-circle-info',
		isActive: false,
		disabled: false,
		id: 'info',
	},
];

export const groupsTabsConfig: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-sliders-simple',
		isActive: true,
		disabled: false,
		id: 'main',
	},
	{
		icon: 'fat fa-microchip',
		isActive: false,
		disabled: false,
		id: 'devices',
	},
	{
		icon: 'fat fa-notebook',
		isActive: false,
		disabled: false,
		id: 'events',
	},
	{
		icon: 'fat fa-wand-magic-sparkles',
		isActive: false,
		disabled: false,
		id: 'info',
	},
];

export const deviceTabsConfig: SidepanelTabsPropsTypes['tabs'] = [...DEFAULT_TABS_CONFIG];

export const alarmsTabsConfig: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-sliders-simple',
		isActive: true,
		disabled: false,
		id: 'main',
	},
	{
		icon: 'fat fa-microchip',
		isActive: false,
		disabled: false,
		id: 'devices',
	},
	{
		icon: 'fat fa-notebook',
		isActive: false,
		disabled: false,
		id: 'events',
	},
	{
		icon: 'fat fa-circle-info',
		isActive: false,
		disabled: false,
		id: 'info',
	},
];

export const dashTabsConfig = [...DEFAULT_TABS_CONFIG];

export const thermostatTabsConfig: SidepanelTabsPropsTypes['tabs'] = [
	{
		icon: 'fat fa-temperature-half',
		isActive: true,
		disabled: false,
		id: 'main',
	},
	{
		icon: 'fat fa-chart-line',
		isActive: false,
		disabled: false,
		id: 'chart',
	},
	{
		icon: 'fat fa-microchip',
		isActive: false,
		disabled: false,
		id: 'devices',
	},
	{
		icon: 'fak fa-light-sensor-on-gear',
		isActive: false,
		disabled: false,
		id: 'fav',
	},
	{
		icon: 'fat fa-circle-info',
		isActive: false,
		disabled: false,
		id: 'info',
	},
];

export const handleSidePanelTabClick = (tabs: SidepanelTabsPropsTypes['tabs'], index: number) => {
	tabs = tabs.map((t, i) => ({
		...t,
		isActive: i === index,
	}));

	return tabs;
};

export type SidepanelTabsPropsTypesExtended = {
	tabs: (SidepanelTabPropsTypes & { component?: any })[];
};

/**
 * Tab components that should NOT be disabled when device is offline/rebooting/updating/calibrating.
 */
export const ENABLED_TABS_FOR_OFFLINE_STATES = new Set([
	'main',
	'details',
	'location',
	'charts',
	'settings',
	'share',
	'event_log',
	'activity_log',
	'statistics',
	'addons',
	'application_settings',
	'notifications',
	'device_information',
	'networks',
	'history',
	'camera_recordings',
]);

/**
 * Sub menus components that are allowed when device is offline/rebooting/updating/calibrating.
 * change_view (Application settings) is intentionally excluded so it stays disabled when offline.
 */
export const ENABLED_MENUS_FOR_OFFLINE_STATE = new Set([
	'wifi_mode_client',
	'wifi_client_backup',
	'wifi_mode_ap',
	'range_extender',
	'outbound_websocket',
	'rpc_over_udp',
	'mqtt',
	'ai_model',
	'offline_notifications',
	'device_card_customization',
	'appliance_type',
	'consumption_calculation',
	'consumption_hierarchy',
	'pin_lock',
	'exclude_event_log',
	'exclude_forgotten_lights',
	'device_note',
	'device_usage_details',
	'device_information',
	'transfer_device',
	'delete_device',
	'input_state',
]);

/**
 * Tab components that are allowed when device is shared with other users.
 */
export const SHARED_DEVICE_ALLOWED_TABS_AND_MENUS = new Set([
	'main',
	'details',
	'location',
	'application_settings',
	'share',
	'settings',
	'device_information',
	'delete_device',
	'notifications',
	'event_log',
	'history',
	'statistics',
	'addons',
	'activity_log',
	'device_card_customization',
	'secondary_controls',
	'pin_lock',
]);

/**
 * Determines if a SidePanel device tab should be disabled based on device state and conditions.
 *
 * Logic is derived from DeviceDrawer.svelte (lines 1212-1234) to ensure consistent tab behavior
 * across both DeviceDrawer and SidePanel layouts. The function checks three main conditions:
 *
 * 1. Always enabled tabs: Main tab (index 0) and tabs in ENABLED_TABS_FOR_OFFLINE_STATES
 *    (charts, settings, share, notifications, statistics, activity_log, etc.) are never disabled.
 *
 * 2. Shared devices: All tabs are disabled except application_settings, share, settings,
 *    device_information, notifications, and event_log. This prevents unauthorized access
 *    to device controls when the device is shared with other users.
 *
 * 3. Offline/problematic states: When deviceState.type < defaultStateType (99), the device
 *    is in a problematic state (0=offline, 2=rebooting, 3=updating, 4=calibrating). In these
 *    states, tabs are disabled to prevent user interaction with unavailable device features,
 *    except for networks tab which remains accessible for troubleshooting.
 *
 * @param device - Device object containing device properties including shared status
 * @param deviceState - Device state object from getDeviceState store (use $deviceState for reactivity)
 * @param tabComponent - Optional component for tab-specific checks (e.g., 'Timing', 'Notifications')
 * @returns true if the tab should be disabled, false if it should be enabled
 */
export function isTabDisabled(
	device: Device | undefined,
	deviceState: any,
	tabComponent: string,
): boolean {
	if (!device?.shared) {
		if (checkDeviceForceUpdate(device) && tabComponent === 'firmware_version') {
			return false;
		}

		/**
		 * Checks if the device state is below the default state type.
		 * States below defaultStateType (99) indicate problematic conditions:
		 * 0=offline, 2=rebooting, 3=updating, 4=calibrating, etc.
		 */
		if (deviceState.type === defaultStateType) return false;

		if (ENABLED_TABS_FOR_OFFLINE_STATES.has(tabComponent)) {
			return false;
		}

		if (ENABLED_MENUS_FOR_OFFLINE_STATE.has(tabComponent)) {
			return false;
		}
	} else {
		if (SHARED_DEVICE_ALLOWED_TABS_AND_MENUS.has(tabComponent)) {
			return false;
		}
	}
	return true;
}
